import type { CapacityBlock, CapacityLine, LimitPair } from '@bbbk/schema';
import { AREA_LABELS, CAPACITY_LINES, rangeLabel as rangeLabelOf } from '@bbbk/schema';

// Settings › Customer portal › Size limits (portal-foundations AC15) — the
// presentational rules, kept out of the component so they can be tested
// directly. The component renders; this decides what the words are.
//
// SCOPE. This screen is the two standing limits and the dated override list,
// and nothing else. The rest of R-SET-1's keys exist and default correctly
// (AC11) but the page that reads them is `availability-and-request`'s.

// `0` reads as CLOSED, not as a number (AC15's own words). A limit column
// showing "0" reads as a broken value or a missing one; the word says what
// Patricia meant when she typed it.
export function limitLabel(limit: number): string {
  return limit === 0 ? 'Closed' : String(limit);
}

// The size, in the words the rest of the desk uses. Borrowed from AREA_LABELS
// rather than re-cased here: `big` reads as "Big" on every pill and filter in
// the app already, and a second casing rule is a second thing to keep in step.
export function sizeLabel(line: CapacityLine): string {
  // The kennel line is every dog whatever its size, so it has no AREA_LABEL —
  // it is not an area. The two size lines borrow the app-wide labels rather
  // than re-casing, so "Big" reads the same here as on every pill and filter.
  return line === 'kennel' ? 'Whole kennel' : AREA_LABELS[line];
}

// MOVED to @bbbk/schema, and re-exported here so every call site is unchanged.
// The API composes the overlap refusal and had no formatter, so the toast said
// "2027-12-12 to 2027-12-19" while the list beneath it said "12 Dec 2027 to
// 19 Dec 2027" — one range, two spellings, two inches apart. Seen in the
// browser; no test could have caught it, because no test rendered both.
export { dayLabel, rangeLabel } from '@bbbk/schema';

// One row's whole sentence, for the screen reader and for the row's title. The
// list is a set of rules and each row should read as one.
export function blockSentence(block: CapacityBlock): string {
  const who = block.line === 'kennel' ? 'the kennel' : `${block.line} dogs`;
  if (block.hardLimit === 0) return `${rangeLabelOf(block)} — closed to ${who}`;
  const warn = block.softLimit === null ? '' : `, limited from ${block.softLimit}`;
  return `${rangeLabelOf(block)} — at most ${block.hardLimit} ${who}${warn}`;
}

// Soonest first, then by size, so the list reads as a calendar. Matches the
// API's own ORDER BY rather than re-deciding it — a client that sorted
// differently from the server would reorder the list on every reload.
export function sortBlocks(blocks: readonly CapacityBlock[]): CapacityBlock[] {
  return [...blocks].sort((a, b) => a.startsOn.localeCompare(b.startsOn) || a.line.localeCompare(b.line));
}

// --- the draft row ---------------------------------------------------------

export interface BlockDraft {
  line: CapacityLine;
  startsOn: string;
  endsOn: string;
  // Form fields, so strings until they are validated. `hard` is the red line
  // (0 = closed); `soft` is the amber one and is OPTIONAL — a closed week wants
  // no warning band.
  hard: string;
  soft: string;
  // WHY the range is closed. Optional — Patricia closing a week without typing
  // a reason is not an error — but the column existed from migration 0027 and
  // the form had no way to write it, so every row she could add said nothing.
  note: string;
}

// The reason a range is closed, at most. Mirrors MAX_NOTE_LENGTH in the API's
// capacity/query.ts, which is the authority; this one exists so the Add button
// refuses before a round trip rather than after one.
export const MAX_NOTE_LENGTH = 120;

export const EMPTY_DRAFT: BlockDraft = { line: 'big', startsOn: '', endsOn: '', hard: '', soft: '', note: '' };

/**
 * What the Add button can send, or why it cannot.
 *
 * A CLIENT-SIDE MIRROR of the API's rules, and deliberately not the authority:
 * the route validates the same things and refuses overlaps against the stored
 * rows, which is a question no client can answer without a race. This exists so
 * the obvious mistakes are caught before a round trip — an empty date, a
 * reversed range — and so the Add button can be disabled rather than offering
 * an action that is going to fail.
 */
export function validateDraft(draft: BlockDraft): { error: string } | null {
  if (!draft.startsOn) return { error: 'Pick a first day.' };
  if (!draft.endsOn) return { error: 'Pick a last day.' };
  if (draft.endsOn < draft.startsOn) return { error: 'That range ends before it starts.' };
  const hard = parseLimit(draft.hard);
  if (hard === null) {
    return { error: 'The hard limit is a whole number — 0 means closed.' };
  }
  if (draft.soft.trim() !== '') {
    const soft = parseLimit(draft.soft);
    if (soft === null) return { error: 'The soft limit is a whole number.' };
    if (soft > hard) {
      // Refused, unlike a size line above the kennel line — that one is a
      // legitimate configuration where a line never binds. An inverted pair
      // silently deletes the amber band and is a typo every time.
      return { error: 'The soft limit cannot be above the hard limit.' };
    }
  }
  if (draft.note.trim().length > MAX_NOTE_LENGTH) {
    return { error: `Keep the reason shorter than ${MAX_NOTE_LENGTH} characters.` };
  }
  return null;
}

/** The body the draft POSTs, once it is valid. */
export function draftToBody(draft: BlockDraft): {
  line: CapacityLine;
  startsOn: string;
  endsOn: string;
  hardLimit: number;
  softLimit: number | null;
  note: string | null;
} {
  return {
    line: draft.line,
    startsOn: draft.startsOn,
    endsOn: draft.endsOn,
    hardLimit: Number(draft.hard),
    // Blank means NO amber band on this block, not "inherit the standing one".
    softLimit: draft.soft.trim() === '' ? null : Number(draft.soft),
    // NULL for a blank one, never an empty string. The column is nullable and
    // the row renders `note ?? ''`; an empty string stored is a reason that
    // exists and says nothing.
    note: draft.note.trim() === '' ? null : draft.note.trim(),
  };
}

// --- ONE limit rule, for the standing fields and the draft alike ------------
//
// PR #204 review, and a real one. There were two copies of this: validateDraft
// guarded a blank (`draft.limit.trim() === ''`) and the standing-limit save did
// not. `Number('')` is 0, and `Number.isInteger(0) && 0 >= 0` both pass — so
// clearing the Big field and pressing Save stored 0, which means CLOSED. The
// kennel shut to big dogs on a mis-click, silently.
//
// The fix is not a second guard, it is one rule. Both callers go through here.

/** A limit field's value, or null if it is not a whole non-negative number. */
export function parseLimit(raw: string): number | null {
  if (raw.trim() === '') return null;
  const n = Number(raw);
  return Number.isInteger(n) && n >= 0 ? n : null;
}

/**
 * Whether the Save button should be live. A BLANK field is not a change — it is
 * a field mid-edit. Reading it as one is the other half of the bug above: the
 * old rule was `Number(edits[size]) !== standing[size]`, so clearing a 20 gave
 * `0 !== 20` and armed Save on an empty box.
 */
export type LimitKey = `${CapacityLine}_${'soft' | 'hard'}`;
export const LIMIT_KEYS: LimitKey[] = CAPACITY_LINES.flatMap(
  (line) => [`${line}_soft`, `${line}_hard`] as LimitKey[],
);

export function isDirty(
  edits: Partial<Record<LimitKey, string>>,
  standing: Record<CapacityLine, LimitPair>,
): boolean {
  return LIMIT_KEYS.some((key) => {
    const raw = edits[key];
    if (raw === undefined) return false;
    const parsed = parseLimit(raw);
    if (parsed === null) return false;
    const [line, which] = key.split('_') as [CapacityLine, 'soft' | 'hard'];
    return parsed !== standing[line][which];
  });
}

/**
 * The settings writes a standing-limit save authorises, or why it cannot.
 *
 * Refuses the WHOLE save when any edited field is invalid, rather than sending
 * the good half — a save that applied one size and dropped the other would
 * leave Patricia's screen disagreeing with the database with no way to tell
 * which half landed.
 */
export function standingEdits(
  edits: Partial<Record<LimitKey, string>>,
  standing: Record<CapacityLine, LimitPair>,
): { writes: Record<string, number> } | { error: string } {
  const writes: Record<string, number> = {};
  const resolved = { ...standing };

  for (const key of LIMIT_KEYS) {
    const raw = edits[key];
    if (raw === undefined) continue;
    const parsed = parseLimit(raw);
    const [line, which] = key.split('_') as [CapacityLine, 'soft' | 'hard'];
    if (parsed === null) {
      return { error: `The ${sizeLabel(line).toLowerCase()} ${which} limit is a whole number — 0 means closed.` };
    }
    resolved[line] = { ...resolved[line], [which]: parsed };
    if (parsed !== standing[line][which]) writes[`capacity_${key}`] = parsed;
  }

  // Checked against the RESOLVED pair, not the edited half: raising a hard line
  // and its soft in one save must not be refused because the soft was compared
  // against the hard's old value.
  for (const line of CAPACITY_LINES) {
    const { soft, hard } = resolved[line];
    if (soft !== null && soft > hard) {
      return { error: `The ${sizeLabel(line).toLowerCase()} soft limit cannot be above its hard limit.` };
    }
  }
  return { writes };
}

// --- the standing limits ---------------------------------------------------

/**
 * The two standing numbers, read out of the settings block. Strings on the wire
 * (settings.value is text), numbers on the screen.
 *
 * NO CROSS-CHECK against `capacity_hard`, and that is the decision rather than
 * an omission. The seeded 20 + 40 sums to less than 65, which means the kennel
 * line never binds and the size limits are the only thing that refuses a stay.
 * That is allowed, it is the state the migration ships with, and a screen that
 * warned about it would be wrong about its own defaults on day one. Patricia
 * raises either number past the hard line to put the kennel back in charge.
 */
export function standingLimits(portal: Record<string, string>): Record<CapacityLine, LimitPair> {
  const pair = (line: CapacityLine): LimitPair => ({
    soft: Number(portal[`capacity_${line}_soft`] ?? 0),
    hard: Number(portal[`capacity_${line}_hard`] ?? 0),
  });
  // Built FROM the vocabulary, not from three names typed out. Adding `house`
  // to CAPACITY_LINES broke this line and nothing else, which is the only
  // reason the omission was visible — a hand-written list would have compiled
  // and quietly dropped the house from every screen that reads limits.
  return Object.fromEntries(
    CAPACITY_LINES.map((line) => [line, pair(line)]),
  ) as Record<CapacityLine, LimitPair>;
}
